import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Paper,
  Card,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { ToastError, ToastSuccess } from "../../utils/Toaster/Toaster";
import { createEarnedLeaveRequest } from "../../services/earnedLeaveRequest.service";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import TextArea from "antd/es/input/TextArea";

const ApplyEarnedLeave = (props) => {
  const { setOpenDialog, GetData } = props;
  const { accessToken } = useAuth();
  const formRef = useRef(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isHalfDay, setIsHalfDay] = useState(false);

  const getUserIdFromToken = () => {
    if (!accessToken) return null;

    try {
      const decoded = jwtDecode(accessToken);
      return decoded.id;
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  };

  const userId = getUserIdFromToken();

  const initialValues = {
    reason: "",
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string()
      .required("Reason is required")
      .min(10, "Reason must be at least 10 characters"),
  });

  const handleAddDate = () => {
    if (!selectedDate) {
      ToastError("Please select a date");
      return;
    }

    const dateString = dayjs(selectedDate).format("YYYY-MM-DD");
    if (selectedDates.some((date) => date === dateString)) {
      ToastError("This date is already added");
      return;
    }

    setSelectedDates([...selectedDates, dateString]);
    setSelectedDate(null);
  };

  const handleRemoveDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((date) => date !== dateToRemove));
  };

  const onSubmit = async (values) => {
    try {
      if (selectedDates.length === 0) {
        ToastError("Please add at least one date");
        return;
      }

      const payload = {
        dates: selectedDates,
        reason: values.reason,
        isHalfDay: isHalfDay,
      };

      const res = await createEarnedLeaveRequest(userId, payload);
      ToastSuccess(res.message);
      setOpenDialog(false);
      if (GetData) GetData();
    } catch (error) {
      ToastError(error?.data?.message || "Failed to submit request");
    }
  };

  // No date restriction - allow any date
  const shouldDisableDate = (date) => {
    return false;
  };

  return (
    <div className="addRenderingPopup">
      <Box className="addContainerHeader">
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
          Request Earned Leave
        </Typography>
        <CloseIcon
          className="Close-Icon"
          onClick={() => setOpenDialog(false)}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Divider sx={{ opacity: 1 }} />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={formRef}
      >
        {({ touched, errors, values, setFieldValue, handleChange, handleBlur }) => (
          <Form>
            <DialogContent
              sx={{
                p: { xs: 2, sm: 3 },
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" }
              }}
            >
              <Grid container spacing={2}>
                {/* Instructions */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: "#e3f2fd", borderRadius: 1, borderLeft: "4px solid #2196f3" }}>
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                      <AssignmentIcon fontSize="small" /> Instructions
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Select the dates you worked extra. Each date will create a separate request.
                    </Typography>
                  </Box>
                </Grid>

                {/* Date Selection */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Select Dates <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        format="DD/MM/YYYY"
                        // slotProps={{
                        //       popper: {
                        //         placement: "left",
                        //       },
                        //     }}
                        slotProps={{
                          popper: {
                            placement: "right",
                          },
                          textField: {
                            size: "small",
                            fullWidth: true,
                            placeholder: "Select Date",
                            error: false
                          },
                        }}
                        sx={{ flexGrow: 1 }}
                      />
                    </LocalizationProvider>
                    <Button
                      variant="contained"
                      onClick={handleAddDate}
                      disabled={!selectedDate}
                      sx={{
                        height: "40px",
                        bgcolor: "#008080",
                        "&:hover": { bgcolor: "#006666" },
                        textTransform: "capitalize"
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                {/* Selected Chips */}
                {selectedDates.length > 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {selectedDates.map((date) => (
                        <Chip
                          key={date}
                          label={`${dayjs(date).format("DD/MM/YYYY")} (${dayjs(date).format("ddd")})`}
                          onDelete={() => handleRemoveDate(date)}
                          size="small"
                          sx={{
                            bgcolor: "#008080",
                            color: "white",
                            "& .MuiChip-deleteIcon": {
                              color: "white",
                              "&:hover": { color: "#e0e0e0" }
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Half Day Checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isHalfDay}
                        onChange={(e) => setIsHalfDay(e.target.checked)}
                        sx={{
                          color: "#008080",
                          "&.Mui-checked": {
                            color: "#008080",
                          },
                        }}
                      />
                    }
                    label="Half Day Leave"
                  />
                </Grid>

                {/* Reason Input */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Reason <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <TextArea
                    fullWidth
                    multiline
                    minRows={4}
                    name="reason"
                    placeholder="Enter Reason"
                    value={values.reason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    status={touched.reason && errors.reason ? "error" : ""}
                    style={{
                      color: "#000000", // Ensure text is visible
                    }}
                  />
                  {touched.reason && errors.reason && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.reason}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <Divider sx={{ opacity: 1 }} />
            <Grid className="addboxFooter">
              <Button
                className="cancelButton"
                onClick={() => setOpenDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="saveButton"
                disabled={selectedDates.length === 0}
              >
                Submit
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );


};

export default ApplyEarnedLeave;
